import {Component, Inject, OnInit, signal} from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms';
import {MatDialogModule, MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog';
import {CommonModule} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {AuthService} from '../../../core/auth/auth.service';
import {AuthorisationService} from '../../../core/services/authorisation.service';
import {
  TextFieldComponent,
  DialogCloseButtonComponent,
  SecondaryButtonComponent,
  PrimaryButtonComponent,
  InfoMessageComponent
} from '@q9elements/ui-kit/components';
import {finalize} from 'rxjs';
import {getAuthorisationErrors} from '../../shared/utils/get-authorisation-errors';
import {Q9Validators} from '@q9elements/ui-kit/validators';

interface SignOffAuthorisationDialogData {
  all?: boolean;
  numberOfDiagrams?: number;
  diagramId?: string;
}

@Component({
  selector: 'app-sign-off-authorisation',
  templateUrl: './sign-off-authorisation.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    TranslateModule,
    TextFieldComponent,
    DialogCloseButtonComponent,
    SecondaryButtonComponent,
    PrimaryButtonComponent,
    InfoMessageComponent
  ],
  styles: [
    `
      .modal-info {
        margin-bottom: 32px;
      }
    `
  ]
})
export class SignOffAuthorisationComponent implements OnInit {
  public form: UntypedFormGroup;
  public havePassword = false;
  public error: string;
  public numberOfDiagrams = 0;
  public isLoading = signal(false);
  public isSingle: boolean;

  private MIN_PASSWORD_CHARACTERS = 6;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SignOffAuthorisationDialogData,
    private dialogRef: MatDialogRef<SignOffAuthorisationComponent>,
    private authService: AuthService,
    private authorisationService: AuthorisationService
  ) {}

  ngOnInit() {
    this.isSingle = !this.data.all;
    this.numberOfDiagrams = this.data && this.data.numberOfDiagrams;
    this.initAuthorisationFormSignOff();
  }

  public get passwordControl() {
    return this.form.get('password') as AbstractControl;
  }

  public onSubmit() {
    if (this.form.invalid || this.isLoading()) {
      return;
    }

    this.isLoading.set(true);
    const password = this.passwordControl?.value ?? '';
    const {diagramId} = this.data;
    const request = this.isSingle
      ? this.authorisationService.signOffTask({diagramId, password})
      : this.authorisationService.signOffAllTask({password});

    request.pipe(finalize(() => this.isLoading.set(false))).subscribe({
      next: res => this.dialogRef.close(res),
      error: ({error}) => (this.error = getAuthorisationErrors(error, this.passwordControl))
    });
  }

  private initAuthorisationFormSignOff() {
    this.form = new UntypedFormGroup({});
    const user = this.authService.getUserDataSync();

    this.havePassword = user.havePassword;

    if (this.havePassword) {
      this.form.addControl(
        'password',
        new UntypedFormControl('', [
          Q9Validators.required(),
          Q9Validators.minLength(this.MIN_PASSWORD_CHARACTERS)
        ])
      );
    }
  }
}
